import { useCallback, useEffect, useState } from 'react';
import { Alert, App, Button, Modal, Spin } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { getEvidenceFile } from '@/api/entities';
import { normalizeApiError } from '@/api/errors';
import type { EvidenceView } from '@/api/types';
import { PdfPreview } from './PdfPreview';
import { evidenceKind, previewKindOf } from './evidenceModel';

type View =
  | { state: 'loading' }
  | { state: 'ready'; blob: Blob }
  | { state: 'erased' }
  | { state: 'no-file' }
  | { state: 'failed'; message: string };

const OCTET_STREAM = 'application/octet-stream';

function ImagePreview({ blob, onFailed }: { blob: Blob; onFailed: () => void }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const created = URL.createObjectURL(blob);
    setUrl(created);
    return () => URL.revokeObjectURL(created);
  }, [blob]);
  if (!url) return null;
  return (
    <div style={{ textAlign: 'center' }}>
      <img src={url} alt="Stored document" data-testid="image-preview" onError={onFailed} style={{ maxWidth: '100%', maxHeight: '70vh' }} />
    </div>
  );
}

/**
 * Opens a stored file (OL-26 slice 4). Only the types in `previewKindOf`, judged from what the server said
 * it was serving, are drawn: a PDF onto a canvas, an image into an img, where a file's scripts cannot
 * run. Nothing is ever put in a frame, an object or the DOM as markup, and anything else is offered as a
 * download with a type no browser will open. A file displays as stored: nothing here masks it.
 */
export function DocumentPreviewModal({
  entityId,
  evidence,
  onClose,
  onErased,
}: {
  entityId: string;
  evidence: EvidenceView;
  onClose: () => void;
  /** The record was erased after this page loaded, so the page is out of date. */
  onErased: () => void;
}) {
  const { message } = App.useApp();
  const [view, setView] = useState<View>({ state: 'loading' });
  const [unshowable, setUnshowable] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const hash = evidence.contentHash;

  useEffect(() => {
    let live = true;
    setView({ state: 'loading' });
    setUnshowable(false);
    getEvidenceFile(entityId, hash, 'PREVIEW').then(
      ({ blob }) => {
        if (live) setView({ state: 'ready', blob });
      },
      (e: unknown) => {
        if (!live) return;
        const error = normalizeApiError(e);
        if (error.code === 'EVIDENCE_ERASED') {
          setView({ state: 'erased' });
          onErased();
        } else if (error.code === 'EVIDENCE_NO_FILE') {
          setView({ state: 'no-file' });
        } else {
          setView({ state: 'failed', message: error.message });
        }
      },
    );
    return () => {
      live = false;
    };
  }, [entityId, hash, onErased]);

  const failedToShow = useCallback(() => setUnshowable(true), []);

  const download = async () => {
    setDownloading(true);
    try {
      // A fresh read, so the download has its own audit record. The type stops the browser opening it.
      const { blob, fileName } = await getEvidenceFile(entityId, hash, 'DOWNLOAD');
      const url = URL.createObjectURL(new Blob([blob], { type: OCTET_STREAM }));
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (e) {
      void message.error(normalizeApiError(e).message);
    } finally {
      setDownloading(false);
    }
  };

  const kind = view.state === 'ready' && !unshowable ? previewKindOf(view.blob.type) : null;
  const canDownload = view.state === 'ready' || view.state === 'failed';
  return (
    <Modal
      open
      width={920}
      title={evidenceKind(evidence) ?? 'Document'}
      onCancel={onClose}
      footer={[
        canDownload && (
          <Button key="download" icon={<DownloadOutlined aria-hidden />} loading={downloading} onClick={() => void download()} data-testid="preview-download">
            Download
          </Button>
        ),
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
    >
      <div data-testid="document-preview" data-state={view.state} style={{ minHeight: 160, maxHeight: '75vh', overflow: 'auto' }}>
        {view.state === 'loading' && (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin aria-label="Loading the file" />
          </div>
        )}
        {view.state === 'erased' && (
          <Alert
            type="warning"
            showIcon
            data-testid="preview-erased"
            message="This file was erased"
            description="The subject's data was erased. The record and its hash remain as proof it existed."
          />
        )}
        {view.state === 'no-file' && <Alert type="info" showIcon data-testid="preview-no-file" message="No file is stored for this record" />}
        {view.state === 'failed' && <Alert type="error" showIcon data-testid="preview-failed" message="The file could not be opened" description={view.message} />}
        {view.state === 'ready' && kind === 'pdf' && <PdfPreview blob={view.blob} onFailed={failedToShow} />}
        {view.state === 'ready' && kind === 'image' && <ImagePreview blob={view.blob} onFailed={failedToShow} />}
        {view.state === 'ready' && !kind && (
          <Alert
            type="info"
            showIcon
            data-testid="preview-fallback"
            message="This file can't be shown here"
            description={
              unshowable
                ? 'It may be damaged or password-protected. Download it to open it on your computer.'
                : view.blob.type === OCTET_STREAM
                  ? 'Its type could not be confirmed. Download it, and open it with care.'
                  : 'Download it to open it on your computer.'
            }
          />
        )}
      </div>
    </Modal>
  );
}
